import { useState } from 'react'
import type { EInvoiceBuyer } from '../lib/einvoice'
import { Sheet } from './Sheet'

interface Props {
  value: EInvoiceBuyer | null
  onSave: (buyer: EInvoiceBuyer | null) => void
  onClose: () => void
}

/**
 * Thông tin người mua trên hoá đơn điện tử.
 *
 * Bốn ô, và cả bốn đều để trống được. Hoá đơn khởi tạo từ máy tính tiền không bắt buộc có
 * thông tin người mua — khách mua ly cà phê thì không ai đứng khai mã số thuế, và bắt gõ
 * cho đủ là làm chậm hàng người đang đợi trả tiền. Ô nào cần thì khách tự đọc.
 *
 * Mã số thuế kiểm ngay tại chỗ: gõ sai rồi phát hành thì tờ hoá đơn đã lên cơ quan thuế,
 * sửa lại là phải lập hoá đơn thay thế.
 */
export function BuyerSheet({ value, onSave, onClose }: Props) {
  const [name, setName] = useState(value?.name ?? '')
  const [taxCode, setTaxCode] = useState(value?.taxCode ?? '')
  const [address, setAddress] = useState(value?.address ?? '')
  const [email, setEmail] = useState(value?.email ?? '')
  const [problem, setProblem] = useState<string | null>(null)

  function save() {
    const digits = taxCode.replace(/[\s-]/g, '')
    if (digits && !/^\d{10}(\d{3})?$/.test(digits)) {
      setProblem('Mã số thuế phải là 10 số, hoặc 13 số nếu là đơn vị phụ thuộc.')
      return
    }

    const buyer: EInvoiceBuyer = {
      name: name.trim() || null,
      taxCode: digits || null,
      address: address.trim() || null,
      email: email.trim() || null,
    }

    const empty = !buyer.name && !buyer.taxCode && !buyer.address && !buyer.email
    onSave(empty ? null : buyer)
  }

  return (
    <Sheet title="Khách lấy hoá đơn" onClose={onClose}>
      <p className="hint">
        Khách là công ty thì gõ mã số thuế, còn khách lẻ thì để trống hết cũng xuất được hoá đơn.
      </p>

      <label className="field">
        <span>Tên người mua hoặc công ty</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Công ty TNHH Bình Minh"
          maxLength={120}
        />
      </label>

      <label className="field">
        <span>Mã số thuế</span>
        <input
          inputMode="numeric"
          value={taxCode}
          onChange={(e) => {
            setTaxCode(e.target.value)
            setProblem(null)
          }}
          placeholder="0123456789"
          maxLength={16}
        />
      </label>

      <label className="field">
        <span>Địa chỉ</span>
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          maxLength={250}
        />
      </label>

      <label className="field">
        <span>Email nhận hoá đơn</span>
        <input
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ketoan@congty.vn"
          maxLength={120}
        />
      </label>

      {problem && (
        <div className="card warn-card">
          <p className="note">{problem}</p>
        </div>
      )}

      <div className="sheet-actions">
        {value && (
          <button type="button" className="btn btn-danger-ghost" onClick={() => onSave(null)}>
            Bỏ
          </button>
        )}
        <button type="button" className="btn btn-brand grow" onClick={save}>
          Xong
        </button>
      </div>
    </Sheet>
  )
}
